import * as React from 'react';

import Icon from '~/components/icon';
import { useLoaderContext } from '~/providers/loader-context';
import { ModalView, useModalContext } from '~/providers/modal-context';
import TrackingActions, {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_EVENTS } from
'~/services/actions/tracking-actions';
import { AccountPersonalisation } from '~/shared/AccountPersonalisation';
import { myOCLoginModals } from '~/stores/constants';
import {
  comp,
  helpClickHandler,
  removeModalSearchParamsFromUrl } from
'~/utils/us-utils';

import styles from './style/legacy-auth-modal.module.css';

export interface IProps {
  children?: React.ReactNode;
  hideHeader?: boolean;
  initView?: ModalView;
}

export const LegacyAuthModal: React.FC<IProps> = ({
  children,
  hideHeader,
  initView
}) => {
  const legacyLoginSuccessRef = React.useRef(false);

  const trackingActions = React.useMemo(() => new TrackingActions(), []);

  const { baseUrlWithProtocolAndRepub, currentPath, user } = useLoaderContext();

  const [
  { loading, ocAuthReady, refreshPage, searchString, showModal, view },
  dispatch] =
  useModalContext();

  // Helpers

  const getQueryParams = React.useCallback(() => {
    const search =
    searchString || typeof window !== 'undefined' ?
    window.location.search.substring(1) :
    currentPath;
    const params = new URLSearchParams(search);

    return params;
  }, [searchString, currentPath]);

  const getModalParams = React.useCallback(() => {
    const isOCPlus = currentPath.startsWith('/plus/');

    const params = getQueryParams();
    const hasSignupParam = params?.has('signup');
    const hasLoginParam = params?.has('login');
    const hasAccountPersonalisation = params?.has('accountpersonalisation');

    return {
      showSignupModal: !isOCPlus && hasSignupParam,
      showLoginModal: !isOCPlus && hasLoginParam,
      showAccountPersonalisation: hasAccountPersonalisation
    };
  }, [getQueryParams, currentPath]);

  // Set ocAuthReady from window
  React.useEffect(() => {
    if (!dispatch) return;

    window.addEventListener('OC:ocAuthReady', () => {
      dispatch({ type: 'SET_OC_AUTH_READY' });
    });
  }, [dispatch]);

  // Set view, showModal and loading
  React.useEffect(() => {
    const { showAccountPersonalisation, showLoginModal, showSignupModal } =
    getModalParams();

    const modalView = (() => {
      switch (true) {
        case showSignupModal && !user:
          return ModalView.SignupForm;

        case showLoginModal && !user:
          return ModalView.LoginForm;

        case showAccountPersonalisation:
          return ModalView.AccountPersonalisation;

        default:
          return view || initView;
      }
    })();

    const canShowView = !!modalView;
    const canShowChildren = typeof children !== 'undefined';
    const canDisableLoading = !(canShowView || canShowChildren);

    dispatch({
      type: 'SET_SHOW_MODAL_VIEW_AND_LOADING',
      payload: {
        view: modalView,
        showModal: canShowView || canShowChildren,
        loading: canDisableLoading
      }
    });
  }, [searchString, getModalParams, view, initView, user, children, dispatch]);

  // Reload the page
  React.useEffect(() => {
    if (!refreshPage) return;

    window.location.reload();
  }, [refreshPage]);

  // Trigger personalisation flow [subs and legacy modals]
  React.useEffect(() => {
    const shouldOnboardFollowingAuthLogin = user?.shouldOnboard;
    const shouldOnboardFollowingLegacyLogin =
    legacyLoginSuccessRef.current && user && !user?.personalisation;
    if (shouldOnboardFollowingAuthLogin || shouldOnboardFollowingLegacyLogin) {
      dispatch({
        type: 'SET_VIEW',
        payload: {
          view: 'AccountPersonalisation'
        }
      });
    }
  }, [dispatch, user]);

  // Subscribe to login success event [legacy modal only]
  React.useEffect(() => {
    const onLoginSuccess = () => legacyLoginSuccessRef.current = true;
    window.addEventListener('OC:LoggedIn', onLoginSuccess);

    return () => {
      window.removeEventListener('OC:LoggedIn', onLoginSuccess);
    };
  }, []);

  // Invoke window.ocAuth.openAtView methods
  React.useEffect(() => {
    removeModalSearchParamsFromUrl();

    if (!ocAuthReady) return;

    switch (view) {
      case ModalView.LoginForm:
        window.ocAuth.openAtView('LOGIN');
        break;

      case ModalView.LoginVerificationForm:
        window.ocAuth.openAtView('LOGIN_FROM_EMAIL_VERIFICATION');
        break;

      case ModalView.SignupForm:
        window.ocAuth.openAtView('SIGN_UP');
        break;

      default:
        break;
    }
  }, [view, ocAuthReady]);

  // NOTE: we require the FE flag - showModal - in order that
  // we can conditionally render modals according to user events

  const hasChildren = !!children;
  const viewHasModal = (view || '') in ModalView;

  const viewIsMyOCLoginModal = myOCLoginModals.includes(view as string);

  const shouldHideModal = (() => {
    switch (true) {
      // simple cases
      case loading:
      case !showModal:
      case !viewHasModal && !hasChildren:
        return true;

      // complex cases
      case !ocAuthReady && viewIsMyOCLoginModal:
        return true;

      default:
        return false;
    }
  })();

  const shouldHideHeader = (() => {
    switch (true) {
      case hideHeader:
      case viewIsMyOCLoginModal:
        return true;

      default:
        return false;
    }
  })();

  const closeModal = (): void => {
    removeModalSearchParamsFromUrl();
    window.ocAuth.openAtView('Close');
    dispatch({ type: 'HIDE_MODAL' });
  };

  const closeModalAndRefreshPage = (): void => {
    removeModalSearchParamsFromUrl();
    window.ocAuth.openAtView('Close');
    dispatch({ type: 'HIDE_MODAL_AND_REFRESH_PAGE' });
  };

  // Add modal helper methods to window
  React.useEffect(() => {
    if (!dispatch) return;

    window.openModalAtView = (modalView: ModalView): void => {
      dispatch({
        type: 'SET_VIEW',
        payload: {
          view: modalView
        }
      });
    };

    window.closeMyOCLoginModal = (): void => {
      removeModalSearchParamsFromUrl();
      window.ocAuth.openAtView('Close');
    };

    window.closeAllModals = (): void => {
      removeModalSearchParamsFromUrl();
      window.ocAuth.openAtView('Close');
      dispatch({ type: 'HIDE_MODAL' });
    };
  }, [dispatch]);

  const renderHeader = (): JSX.Element | null => {
    if (shouldHideHeader) return null;

    const headerCloseIconClickHandler = (): void => {
      switch (view) {
        default:
          closeModal();
          break;
      }
    };

    const renderHelpIcon = (): JSX.Element => {
      // NOTE: we do not currently have any views that
      // require us to render help icon on cx-web
      return <span />;

      const gtmTrackingPremiumFAQIcon = () => {
        trackingActions.eventTracking({
          event: TRACKING_EVENTS.MYOC_FAQ_ICON,
          eventAction: TRACKING_ACTIONS.myoc_faq_icon,
          eventCategory: TRACKING_CATEGORIES.PREMIUM,
          eventLabel: 'faq_icon'
        });
      };

      const handleHelpClick = () => {
        gtmTrackingPremiumFAQIcon();
        helpClickHandler(baseUrlWithProtocolAndRepub);
      };

      return (
        <span
          className={styles.helpIconWrapper}
          data-testid="help-icon"
          onClick={handleHelpClick}>

					<Icon className={styles.helpIcon} icon="PremiumHelp" />
				</span>);

    };

    const renderCloseIcon = (): JSX.Element => {
      return (
        <span
          className={styles.closeIconWrapper}
          data-testid="close-modal"
          onClick={headerCloseIconClickHandler}>

					<Icon
            className={styles.closeIcon}
            height={24}
            icon="Close"
            width={26} />

				</span>);

    };

    return (
      <div className={styles.modalHeader} data-testid="modal-header">
				{renderHelpIcon()}
				{renderCloseIcon()}
			</div>);

  };

  const renderViewOrChildren = ():
  JSX.Element |
  JSX.Element[] |
  null |
  undefined => {
    // 1. Render Views
    switch (view) {
      case ModalView.AccountPersonalisation:
        return (
          <AccountPersonalisation
            baseUrl={baseUrlWithProtocolAndRepub}
            closeModal={closeModal}
            shouldRenderHeadless={true} />);



      default:
        break;
    }

    // 2. Render Children
    return React.Children.map(children, (child) => {
      const item = child as React.ReactElement;

      // Return item if regular DOM element
      if (typeof item.type === 'string') {
        return item;
      }

      // Return compound component
      return React.cloneElement(item, {
        closeModal,
        closeModalAndRefreshPage
      });
    });
  };

  const renderLoginModal = (): JSX.Element => {
    return <div id="myoc-login-modal"></div>;
  };

  return (
    <div
      className={comp(
        styles.modalBackground,
        shouldHideModal ? styles.hidden : ''
      )}>

			<div className={styles.modalContainer} data-testid="myoc-modal-container">
				{renderHeader()}
				{renderViewOrChildren()}
				{renderLoginModal()}
			</div>
		</div>);

};

LegacyAuthModal.displayName = 'LegacyAuthModal[FormerlyPremiumModal]';